import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';
import { BcmAppDataShareApiService } from '@bcmServices/api-services/app-data-share.api-service';
import { AppDataShareCodeDialogComponent } from '@modules/bcm/@shared/components/app-data-share/app-data-share-code-dialog.component';
import { AppDataShareQrDialogComponent } from '@modules/bcm/@shared/components/app-data-share/app-data-share-qr-dialog.component';
import { AppDataShareWaitDialogComponent } from '@modules/bcm/@shared/components/app-data-share/app-data-share-wait-dialog.component';
import { AppNotificationService } from '@core/services/app-notification.service';
import { TranslationService } from '@core/translation/translation.service';
import { AppDataShareBoatRef, AppDataShareStatusResult } from '@bcmServices/api-services/app-data-share.api-service';

@Injectable({ providedIn: 'root' })
export class AppDataShareImportService {
    constructor(
        private readonly api: BcmAppDataShareApiService,
        private readonly dialog: MatDialog,
        private readonly notifications: AppNotificationService,
    ) {}

    async importViaQr(importTarget: 'person' | 'boat'): Promise<{ personId?: number; boatId?: number; boatIds?: number[]; boats?: AppDataShareBoatRef[] } | null> {
        const qrPayload = await firstValueFrom(
            this.dialog.open(AppDataShareQrDialogComponent, { width: '480px' }).afterClosed()
        );

        if (!qrPayload) {
            return null;
        }

        return this.runImport({ qrPayload, importTarget });
    }

    async importViaCode(importTarget: 'person' | 'boat'): Promise<{ personId?: number; boatId?: number; boatIds?: number[]; boats?: AppDataShareBoatRef[] } | null> {
        const shareCode = await firstValueFrom(
            this.dialog.open(AppDataShareCodeDialogComponent, { width: '420px' }).afterClosed()
        );

        if (!shareCode) {
            return null;
        }

        return this.runImport({ shareCode, importTarget });
    }

    async importShareCode(shareCode: string, importTarget: 'person' | 'boat') {
        return this.runImport({ shareCode, importTarget });
    }

    private async runImport(payload: {
        qrPayload?: string;
        shareCode?: string;
        importTarget: 'person' | 'boat';
    }): Promise<{ personId?: number; boatId?: number; boatIds?: number[]; boats?: AppDataShareBoatRef[] } | null> {
        try {
            if (payload.shareCode) {
                const blocked = await this.warnIfKindMismatch(payload.shareCode, payload.importTarget);
                if (blocked) {
                    return null;
                }
            }

            const request = await firstValueFrom(this.api.createRequest(payload));
            if (!request?.requestUuid) {
                this.notifications.showWarning(TranslationService.translate('person.core.up2boatIdFailed', undefined, 'Datenübernahme fehlgeschlagen.'));
                return null;
            }

            const status = await firstValueFrom(
                this.dialog.open(AppDataShareWaitDialogComponent, {
                    width: '460px',
                    disableClose: true,
                    data: { requestUuid: request.requestUuid, expiresAt: request.expiresAt },
                }).afterClosed()
            ) as AppDataShareStatusResult | null;

            if (!status || status.state !== 'approved') {
                this.notifications.showWarning(this.messageFor(status?.state));
                return null;
            }

            this.notifications.showSuccess(TranslationService.translate('person.core.up2boatIdImported', undefined, 'Daten wurden übernommen.'));
            const boatIds = status.boatIds?.length
                ? status.boatIds
                : (status.boats || []).map(boat => boat.id).filter(Boolean);

            return {
                personId: status.result_bcm_person_id || status.person?.id,
                boatId: status.result_bcm_boat_id || status.boat?.id || boatIds[0],
                boatIds,
                boats: status.boats?.length ? status.boats : boatIds.map(id => ({ id })),
            };
        } catch (error) {
            console.error(error);
            this.notifications.showWarning(this.messageFromError(error));
            return null;
        }
    }

    private async warnIfKindMismatch(shareCode: string, importTarget: 'person' | 'boat'): Promise<boolean> {
        try {
            const lookup = await firstValueFrom(this.api.lookupShareCode(shareCode, importTarget));
            if (lookup?.outcome !== 'kindMismatch') {
                return false;
            }

            this.notifications.showWarning(this.mismatchText(lookup.dataType));
            return true;
        } catch (error) {
            console.error(error);
            return false;
        }
    }

    private mismatchText(dataType?: 'userProfile' | 'boat'): string {
        if (dataType === 'boat') {
            return TranslationService.translate(
                'person.core.up2boatIdIsBoat',
                undefined,
                'Diese Up2Boat-ID gehört zu einem Boot. Lege das Boot damit an.',
            );
        }

        return TranslationService.translate(
            'person.core.up2boatIdIsPerson',
            undefined,
            'Diese Up2Boat-ID gehört zu einer Person. Lege die Person damit an.',
        );
    }

    private messageFromError(error: unknown): string {
        const message = (error as { error?: { message?: string } })?.error?.message;
        if (message === 'person.core.up2boatIdIsBoat') {
            return this.mismatchText('boat');
        }
        if (message === 'person.core.up2boatIdIsPerson') {
            return this.mismatchText('userProfile');
        }
        if (message && !message.includes(' ')) {
            return TranslationService.translate(message, undefined, message);
        }
        if (message) {
            return message;
        }

        return TranslationService.translate('person.core.up2boatIdFailed', undefined, 'Datenübernahme fehlgeschlagen.');
    }

    private messageFor(state?: AppDataShareStatusResult['state']): string {
        switch (state) {
            case 'denied':
                return TranslationService.translate('person.core.up2boatIdDenied', undefined, 'Die Freigabe wurde abgelehnt. Es wurden keine Daten übernommen.');
            case 'cancelled':
                return TranslationService.translate('person.core.up2boatIdCancelled', undefined, 'Die Anfrage wurde abgebrochen. Es wurden keine Daten übernommen.');
            default:
                return TranslationService.translate('person.core.up2boatIdExpired', undefined, 'Die Anfrage ist abgelaufen. Es wurden keine Daten übernommen.');
        }
    }
}
